import { useState, type FormEvent } from 'react';

import { Button } from '@/ui/base/button';
import { Label } from '@/ui/base/label';
import { Input } from '@/ui/base/input';
import { SearchableSelect } from '@/ui/base/searchable-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Textarea } from '@/ui/base/textarea';
import { formatarDinheiro } from '@/nucleo/dinheiro';
import { MoneyInput } from '@/ui/MoneyInput';
import { ResponsiveModal } from '@/ui/ResponsiveModal';

import {
  type Cobranca,
  gerarIdLocal,
  gerarLinhaDigitavelMock,
  gerarNumeroDamsp,
  gerarPixCopiaEColaMock,
  precoPublicoVigente,
  rotuloTipoServico,
  TIPOS_SERVICO_ADMINISTRATIVO,
} from './tipos';

interface CredenciadoOpcao {
  nome: string;
  documento: string;
}

interface NovaCobrancaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Credenciados disponíveis para seleção — vêm da própria fixture da lista, sem inventar novos. */
  credenciados: CredenciadoOpcao[];
  /** Chamado com a cobrança pronta; quem chama decide onde guardá-la (não há API ainda). */
  onCriar: (cobranca: Cobranca) => void;
}

const FORM_INICIAL = {
  credenciado: '',
  tipoServico: '',
  descricao: '',
  valor: '',
  vencimento: '',
};

/**
 * Modal de criar cobrança DAMSP. Sem API ainda: ao salvar, monta a cobrança
 * localmente (com número DAMSP e mocks de PIX/boleto) e devolve para quem
 * chamou atualizar a lista — não há persistência real.
 */
export function NovaCobrancaModal({
  open,
  onOpenChange,
  credenciados,
  onCriar,
}: NovaCobrancaModalProps) {
  const [form, setForm] = useState(FORM_INICIAL);

  const isDirty =
    !!form.credenciado ||
    !!form.tipoServico ||
    !!form.descricao ||
    !!form.valor ||
    !!form.vencimento;

  const handleOpenChange = (next: boolean) => {
    if (!next) setForm(FORM_INICIAL);
    onOpenChange(next);
  };

  const handleSubmit = (evento: FormEvent) => {
    evento.preventDefault();

    const credenciadoSelecionado = credenciados.find((c) => c.nome === form.credenciado);
    if (!credenciadoSelecionado || !form.tipoServico || !form.valor || !form.vencimento) return;

    const novaCobranca: Cobranca = {
      id: gerarIdLocal(),
      numeroDamsp: gerarNumeroDamsp(),
      credenciado: credenciadoSelecionado.nome,
      credenciadoDocumento: credenciadoSelecionado.documento,
      tipoServico: form.tipoServico,
      descricao: form.descricao || undefined,
      valor: form.valor,
      situacao: 'PENDENTE',
      emitidaEm: new Date().toISOString(),
      vencimento: form.vencimento,
      pagoEm: null,
      formaPagamento: null,
      canceladaEm: null,
      pixCopiaECola: gerarPixCopiaEColaMock(),
      linhaDigitavel: gerarLinhaDigitavelMock(),
    };

    onCriar(novaCobranca);
    setForm(FORM_INICIAL);
    onOpenChange(false);
  };

  const podeSalvar = !!form.credenciado && !!form.tipoServico && !!form.valor && !!form.vencimento;

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={handleOpenChange}
      title="Nova cobrança DAMSP"
      description="Gere uma cobrança DAMSP para um serviço administrativo do credenciado."
      desktopMaxWidth="max-w-2xl"
      confirmOnClose={isDirty}
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit" form="nova-cobranca-form" disabled={!podeSalvar}>
            Gerar DAMSP
          </Button>
        </div>
      }
    >
      <form id="nova-cobranca-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label>Credenciado *</Label>
          <SearchableSelect
            value={form.credenciado || null}
            onChange={(valor) => setForm((atual) => ({ ...atual, credenciado: valor ?? '' }))}
            options={credenciados.map((c) => ({
              value: c.nome,
              label: c.nome,
              description: c.documento,
            }))}
            placeholder="Selecione o credenciado..."
            searchPlaceholder="Buscar por nome ou CPF/CNPJ..."
            ariaLabel="Credenciado"
            modal
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="nova-cobranca-tipo">Tipo de serviço *</Label>
          <Select
            value={form.tipoServico}
            onValueChange={(valor) => setForm((atual) => ({ ...atual, tipoServico: valor }))}
          >
            <SelectTrigger id="nova-cobranca-tipo">
              <SelectValue placeholder="Selecione o serviço..." />
            </SelectTrigger>
            <SelectContent>
              {TIPOS_SERVICO_ADMINISTRATIVO.map((tipo) => (
                <SelectItem key={tipo} value={tipo}>
                  {rotuloTipoServico(tipo)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {form.tipoServico && !form.valor && precoPublicoVigente(form.tipoServico) && (
            <p className="text-xs text-muted-foreground">
              Preço público vigente: {formatarDinheiro(precoPublicoVigente(form.tipoServico))}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="nova-cobranca-valor">Valor *</Label>
            <MoneyInput
              id="nova-cobranca-valor"
              value={form.valor}
              onChange={(valor) => setForm((atual) => ({ ...atual, valor }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="nova-cobranca-vencimento">Vencimento *</Label>
            <Input
              id="nova-cobranca-vencimento"
              type="date"
              value={form.vencimento}
              onChange={(evento) =>
                setForm((atual) => ({ ...atual, vencimento: evento.target.value }))
              }
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="nova-cobranca-descricao">Descrição</Label>
          <Textarea
            id="nova-cobranca-descricao"
            value={form.descricao}
            onChange={(evento) =>
              setForm((atual) => ({ ...atual, descricao: evento.target.value }))
            }
            placeholder="Descrição da cobrança..."
            rows={3}
          />
        </div>
      </form>
    </ResponsiveModal>
  );
}

export default NovaCobrancaModal;
